import { KomentasWidget } from './components/KomentasWidget';
import { WidgetConfig } from './types';

export { KomentasWidget } from './components/KomentasWidget';
export * from './types';

// Export auth functions for testing and direct usage
export { createFrontendAuth } from './auth/frontend';
export * from './auth/pkce';

/**
 * Automatically inject the widget CSS file if not already loaded
 * This ensures users only need to include the JS file
 */
function injectWidgetCSS() {
  // Check if CSS is already loaded
  const existingLink = document.querySelector('link[href*="komentas-widget.css"]');
  if (existingLink) {
    return; // CSS already loaded
  }

  // Get the script tag's src to determine the base URL and query params
  const scripts = document.getElementsByTagName('script');
  let cssUrl = '';
  
  for (let i = 0; i < scripts.length; i++) {
    const src = scripts[i].src;
    if (src && src.includes('komentas-widget')) {
      // Extract base URL and query params from script src
      const url = new URL(src);
      const baseUrl = `${url.protocol}//${url.host}${url.pathname.substring(0, url.pathname.lastIndexOf('/'))}`;
      const queryParams = url.search; // Get query params like ?v=20251018_2215
      cssUrl = `${baseUrl}/komentas-widget.css${queryParams}`;
      break;
    }
  }

  // If we found the script URL, inject the CSS
  if (cssUrl) {
    const link = document.createElement('link');
    link.rel = 'stylesheet';
    link.type = 'text/css';
    link.href = cssUrl;
    document.head.appendChild(link);
    console.log('[Komentas] Auto-loaded widget CSS from:', link.href);
  } else {
    console.warn('[Komentas] Could not auto-load CSS - widget script URL not found');
  }
}

// Global initialization function for easy integration
declare global {
  interface Window {
    Komentas: typeof KomentasWidget;
    KomentasWidget: {
      init: (config: WidgetConfig & { container?: string | HTMLElement }) => KomentasWidget;
      version: string;
    };
  }
}

// Auto-initialize if script is loaded with global flag
if (typeof window !== 'undefined') {
  // Auto-inject CSS when script loads
  injectWidgetCSS();
  
  window.Komentas = KomentasWidget;
  
  // Provide convenient init method
  window.KomentasWidget = {
    init: (config: WidgetConfig & { container?: string | HTMLElement }) => {
      const container = config.container || '#komentas-widget';
      const element = typeof container === 'string' 
        ? document.querySelector(container) 
        : container;
      
      if (!element) {
        throw new Error(`Container element not found: ${container}`);
      }
      
      const widgetConfig: WidgetConfig = {
        apiUrl: config.apiUrl,
        projectId: config.projectId,
        articleUrl: config.articleUrl || window.location.href,
        articleTitle: config.articleTitle,
        portalUrl: config.portalUrl,
        usePopup: config.usePopup,
        oauthProviders: config.oauthProviders,
        theme: config.theme,
        enableReactions: config.enableReactions,
        reactionsOnlyMode: config.reactionsOnlyMode,
        showArticleReactions: config.showArticleReactions,
        availableEmotions: config.availableEmotions,
        emotionCategories: config.emotionCategories,
        maxCommentLength: config.maxCommentLength,
        sortBy: config.sortBy,
        showLogoutButton: config.showLogoutButton,
        showBranding: config.showBranding,
        enableStarRatings: config.enableStarRatings,
        starRatingDisplayMode: config.starRatingDisplayMode,
        allowStarRatingUpdate: config.allowStarRatingUpdate,
        enableCommentStarRatings: config.enableCommentStarRatings,
        commentStarRatingDisplayMode: config.commentStarRatingDisplayMode,
        allowCommentStarRatingUpdate: config.allowCommentStarRatingUpdate
      };
      
      console.log('[WIDGET-INIT] Received config from loader:', {
        enableStarRatings: config.enableStarRatings,
        starRatingDisplayMode: config.starRatingDisplayMode,
        allowStarRatingUpdate: config.allowStarRatingUpdate,
        enableCommentStarRatings: config.enableCommentStarRatings,
        allowCommentStarRatingUpdate: config.allowCommentStarRatingUpdate
      });
      console.log('[WIDGET-INIT] Passing to KomentasWidget:', {
        enableStarRatings: widgetConfig.enableStarRatings,
        starRatingDisplayMode: widgetConfig.starRatingDisplayMode,
        allowStarRatingUpdate: widgetConfig.allowStarRatingUpdate,
        enableCommentStarRatings: widgetConfig.enableCommentStarRatings,
        allowCommentStarRatingUpdate: widgetConfig.allowCommentStarRatingUpdate
      });
      
      return new KomentasWidget(element as HTMLElement, widgetConfig);
    },
    version: '1.0.0'
  };
  
  // Auto-initialize widgets on page load
  document.addEventListener('DOMContentLoaded', () => {
    const widgets = document.querySelectorAll('[data-komentas]');
    
    widgets.forEach((element) => {
      const config = {
        apiUrl: element.getAttribute('data-api-url') || 'http://localhost:5011',
        projectId: element.getAttribute('data-project-id') || '',
        articleUrl: element.getAttribute('data-article-url') || window.location.href,
        theme: {
          backgroundColor: element.getAttribute('data-theme') === 'dark' ? '#1a1a1a' : '#ffffff'
        },
        enableReactions: element.getAttribute('data-enable-reactions') !== 'false',
        reactionsOnlyMode: element.getAttribute('data-reactions-only-mode') === 'true',
        maxCommentLength: parseInt(element.getAttribute('data-max-length') || '500'),
        sortBy: (element.getAttribute('data-sort-by') as 'newest' | 'oldest') || 'newest'
      };
      
      new KomentasWidget(element as HTMLElement, config);
    });
  });
}